import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  ApplicantAssessment,
  ApplicantAssessmentStageValue,
  ApplicantScoreWeights,
  JobPostingListItem,
} from '../applicants/applicantTypes';
import type { PersonSummary } from '../personnel/personnelTypes';
import type { RecruitmentUserOption } from '../vacancies/vacancyTypes';
import type {
  AssignmentRequest,
  DeliberationUnitOption,
  ApplicantDeliberation,
  ApplicantDeliberationRequest,
  EndorsementRequest,
  OathTakingRequest,
  DeliberationApplicantRow,
} from './deliberationTypes';

interface DeliberationPostingQuery extends Omit<ListQuery, 'status'> {
  /** Comma-separated org-unit ids. */
  unit?: string;
  /** Comma-separated folders — `pending`, `complete`, `cancelled`. */
  stage?: string;
}

interface DeliberationApplicantQuery extends Omit<ListQuery, 'status'> {
  /** `shortlisted` lists the shortlisted only; absent lists All. */
  shortlist?: string;
}

function toQueryString(query: DeliberationPostingQuery & DeliberationApplicantQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  // Sent whenever present, empty string included — an axis ticked to nothing matches no row.
  if (query.unit !== undefined) params.set('unit', query.unit);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.shortlist) params.set('shortlist', query.shortlist);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const BASE = '/api/deliberations';

/** Deliberate — every read is `deliberations.read`; the one write, the deliberation, is `deliberations.update`. */
export const deliberationApi = {
  /** OPEN postings only — the server fixes the status axis. */
  listPostings: (query: DeliberationPostingQuery, signal?: AbortSignal) =>
    api<PagedResult<JobPostingListItem>>(`${BASE}/postings${toQueryString(query)}`, { signal }),

  getPosting: (postingId: string, signal?: AbortSignal) =>
    api<JobPostingListItem>(`${BASE}/postings/${postingId}`, { signal }),

  /** The posting's live applicants, each with its stage scores. Health stages hidden without the medical key. */
  listApplicants: (postingId: string, query: DeliberationApplicantQuery, signal?: AbortSignal) =>
    api<PagedResult<DeliberationApplicantRow>>(`${BASE}/postings/${postingId}/applicants${toQueryString(query)}`, {
      signal,
    }),

  /** Shortlisted or not, and the note — defaults until the first save. */
  deliberation: (applicantId: string, signal?: AbortSignal) =>
    api<ApplicantDeliberation>(`${BASE}/applicants/${applicantId}/deliberation`, { signal }),

  /** Records or corrects it — `deliberations.update`. */
  // Through the POSTING, so the audit row's parent is it and the decision shows in its History Logs.
  saveDeliberation: (postingId: string, applicantId: string, request: ApplicantDeliberationRequest) =>
    api<ApplicantDeliberation>(`${BASE}/postings/${postingId}/applicants/${applicantId}/deliberation`, {
      method: 'PUT',
      body: request,
    }),

  /** Endorses a SHORTLISTED applicant for appointment — `deliberations.endorse`. */
  endorse: (postingId: string, applicantId: string, request: EndorsementRequest) =>
    api<ApplicantDeliberation>(`${BASE}/postings/${postingId}/applicants/${applicantId}/endorsement`, {
      method: 'PUT',
      body: request,
    }),

  /** Files the endorsement document, replacing any earlier one — an endorsed applicant only. Multipart. */
  attachEndorsementFile: (postingId: string, applicantId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<ApplicantDeliberation>(
      `${BASE}/postings/${postingId}/applicants/${applicantId}/endorsement/attachment`,
      form,
    );
  },

  /** A FETCH then an object URL, never an `<a href>` — the token travels in a header. */
  downloadEndorsementFile: (postingId: string, applicantId: string, signal?: AbortSignal) =>
    apiBlob(`${BASE}/postings/${postingId}/applicants/${applicantId}/endorsement/attachment`, { signal }),

  /** The live accounts for the "Endorsed by" picker — Deliberate's own door, under the endorse key. */
  userOptions: (signal?: AbortSignal) => api<RecruitmentUserOption[]>(`${BASE}/user-options`, { signal }),

  /** Withdraws it; the oath-taking schedule is kept. */
  withdrawEndorsement: (postingId: string, applicantId: string) =>
    api<ApplicantDeliberation>(`${BASE}/postings/${postingId}/applicants/${applicantId}/endorsement`, {
      method: 'DELETE',
    }),

  /** The Schedule of Oath Taking — an endorsed applicant only. */
  /** The planned Assignment — under the endorse key. */
  saveAssignment: (postingId: string, applicantId: string, request: AssignmentRequest) =>
    api<ApplicantDeliberation>(`${BASE}/postings/${postingId}/applicants/${applicantId}/assignment`, {
      method: 'PUT',
      body: request,
    }),

  /** The live units for the Assignment's picker — its own door under the endorse key. */
  unitOptions: (signal?: AbortSignal) => api<DeliberationUnitOption[]>(`${BASE}/unit-options`, { signal }),

  saveOathTaking: (postingId: string, applicantId: string, request: OathTakingRequest) =>
    api<ApplicantDeliberation>(`${BASE}/postings/${postingId}/applicants/${applicantId}/oath-taking`, {
      method: 'PUT',
      body: request,
    }),

  /** Every stage with remarks and files. A health stage arrives `isHidden` without the medical key. */
  assessments: (applicantId: string, signal?: AbortSignal) =>
    api<ApplicantAssessment[]>(`${BASE}/applicants/${applicantId}/assessments`, { signal }),

  /**
   * One stage file — refused for a health stage without the medical key. A FETCH then an object URL,
   * never an `<a href>` at the endpoint: the token travels in a header no navigation sends.
   */
  downloadAssessmentFile: (
    applicantId: string,
    stage: ApplicantAssessmentStageValue,
    attachmentId: string,
    signal?: AbortSignal,
  ) => apiBlob(`${BASE}/applicants/${applicantId}/assessments/${stage}/attachments/${attachmentId}`, { signal }),

  /** Score Points — what every score is weighted by. */
  scoreWeights: (signal?: AbortSignal) => api<ApplicantScoreWeights>(`${BASE}/score-weights`, { signal }),

  /** Display names for a page of applicants — refused whole if any id is not an applicant's; at most 100. */
  names: (personIds: string[], signal?: AbortSignal) =>
    api<PersonSummary[]>(`${BASE}/names`, { method: 'POST', body: { personIds }, signal }),
};
